<template>
  <footer class="w-full">
    <Benefits
      v-show="!hideBenefits && !(isCheckoutPage && showCheckoutPageNewLayout)"
    />
    <div
      v-if="footer && !(isCheckoutPage && showCheckoutPageNewLayout)"
      id="footer-nav"
      class="bg-white text-black"
    >
      <div class="container w-full md:px-4 md:py-[54px] xl:px-20">
        <div class="md:grid md:grid-cols-6 md:gap-10 lg:grid-cols-12">
          <div
            v-for="(group, groupIndex) in footer.groups"
            :key="groupIndex"
            class="overflow-auto md:col-span-3"
          >
            <section
              v-for="(category, categoryIndex) in group"
              :key="categoryIndex"
              class="p-1 md:mb-2.5"
            >
              <Button
                v-if="
                  (category as FooterCategory).items &&
                  (category as FooterCategory).items.length > 0
                "
                :id="`accordion-header-${(category as FooterCategory).key}`"
                type="ghost"
                ignore-capitalisation
                :aria-controls="`accordion-content-${(category as FooterCategory).key}`"
                :aria-expanded="
                  activeCategory === (category as FooterCategory).key
                "
                class="accordion-headline flex w-full max-w-full cursor-pointer items-center justify-between border-t border-white px-[15px] text-lg font-normal leading-6 text-black md:mb-4 md:cursor-auto md:border-0 md:p-0 md:text-xl md:font-bold lg:px-0"
                data-test-id="footer-category-toggle"
                @click="toggleActive((category as FooterCategory).key)"
              >
                <span
                  class="inline-block overflow-hidden text-ellipsis text-start"
                >
                  {{ $t((category as FooterCategory).key) }}
                </span>
                <IconArrowDown class="accordion-chevron md:hidden" />
              </Button>

              <nav
                :id="`accordion-content-${(category as FooterCategory).key}`"
                :aria-labelledby="`accordion-header-${(category as FooterCategory).key}`"
                :aria-hidden="
                  activeCategory !== (category as FooterCategory).key
                "
                :class="[
                  'accordion-content px-[15px] pb-2.5 md:block md:p-0 md:px-2',
                  {
                    hidden: activeCategory !== (category as FooterCategory).key,
                  },
                ]"
                role="region"
              >
                <span
                  v-for="(item, itemIndex) in (category as FooterCategory)
                    .items"
                  :key="itemIndex"
                >
                  <nuxt-link
                    v-if="item.to"
                    class="block py-2.5 text-lg font-normal hover:underline md:text-2lg"
                    data-test-id="footer-category-item-link"
                    :to="item.to"
                    :target="item.target"
                    @click="trackNavigationFooter(item.key)"
                  >
                    {{ item.key }}
                  </nuxt-link>
                  <span
                    v-else-if="item.icon"
                    class="mb-[5px] mr-[5px] inline-block w-[50px]"
                    :title="item.key"
                  >
                    <NuxtLink
                      v-if="item.iconTo"
                      :to="item.iconTo"
                      data-test-id="footer-category-item-with-img-link"
                      target="_blank"
                    >
                      <span class="sr-only">
                        {{ getTargetBlankLabel(item.key, $t) }}
                      </span>
                      <NuxtImg
                        :src="item.icon"
                        sizes="sm:50px"
                        :alt="item.key"
                      />
                    </NuxtLink>
                    <NuxtImg
                      v-else
                      :src="item.icon"
                      sizes="sm:50px"
                      :alt="item.key"
                    />
                  </span>
                  <span v-else class="block py-1 text-lg md:text-2lg">
                    {{ item.key }}
                  </span>
                </span>

                <button
                  v-if="(category as FooterCategory).show_consent_opener"
                  class="block px-0 py-1 text-lg md:text-2lg"
                  data-test-id="footer-category-item-cookie-settings"
                  @click="triggerUsercentricsSettings"
                >
                  {{ $t('footer.category.item.cookie_settings') }}
                </button>
              </nav>
            </section>
            <div v-if="groupIndex === footer.groups.length - 1" class="flex">
              <div
                class="box-border flex w-full flex-col justify-center gap-1.5 space-y-2 bg-white py-4 text-center text-black md:w-[200px] md:justify-start md:rounded-md md:text-start"
              >
                <p class="text-lg font-bold">
                  {{ blok.contact_info_title }}
                </p>
                <p class="text-base font-normal">
                  {{ blok.contact_info_text }}
                </p>
                <a
                  :href="`tel:${blok.contact_info_phone}`"
                  rel="noopener noreferrer"
                  class="lg:text-base lg:font-bold"
                >
                  {{ blok.contact_info_phone }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BottomFooter :blok="blok" />
  </footer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import BottomFooter from './BottomFooter.vue'
import Benefits from '~/components/layout/footers/Benefits.vue'
import type {
  SbFooter,
  SbFooterNavigationSection,
  SbFooterInfoItem,
  SbFooterNavigationItem,
} from '~/cms/storyblok/types/storyblok.gen'
import { useRoute } from '#app/composables/router'
import { getTargetBlankLabel } from '~/utils/a11y'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

const { trackNavigationFooter } = useTrackingEvents()

type FooterCategory = {
  key: string
  items: {
    key: string
    icon?: string
    iconTo?: string
    to?: string
    target?: string
  }[]
  show_consent_opener?: boolean
}

type FooterProps = {
  blok: SbFooter
  hideBenefits?: boolean
}

const props = withDefaults(defineProps<FooterProps>(), {
  hideBenefits: false,
})

const { showCheckoutPageNewLayout } = useExperiments()
const route = useRoute()
const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

const triggerUsercentricsSettings = () => {
  ;(window as any)?.UC_UI?.showSecondLayer()
}

const activeCategory = ref<string | null>(null)

const toggleActive = (categoryKey: string) => {
  activeCategory.value =
    activeCategory.value === categoryKey ? null : categoryKey
}

const { sections, payments, carriers, security } = props.blok

const footer = {
  groups: [
    ...(sections
      ?.filter(visiblePredicate)
      .map((section: SbFooterNavigationSection) => [
        {
          ...section,
          key: section.title,
          items:
            section.items?.filter(visiblePredicate).map((item) => ({
              key: item.label,
              to: resolveStoryblokLink({ link: item.link }),
              target: item.link?.target,
            })) || [],
        },
      ]) ?? []),
    [
      ...(payments?.length
        ? [
            payments?.length && {
              key: 'footer.category.payment.label',
              items: payments.filter(visiblePredicate).map(mapInfoItem),
            },
          ]
        : []),
      ...(carriers?.length
        ? [
            {
              key: 'footer.category.carrier.label',
              items: carriers.filter(visiblePredicate).map(mapInfoItem),
            },
          ]
        : []),
      ...(security?.length
        ? [
            {
              key: 'footer.category.security.label',
              items: security.filter(visiblePredicate).map(mapInfoItem),
            },
          ]
        : []),
    ],
  ],
}

function mapInfoItem(item: SbFooterInfoItem) {
  return {
    key: item.title,
    icon: item.icon?.filename,
    iconTo: item.link?.cached_url,
  }
}

function visiblePredicate(
  item: SbFooterNavigationItem | SbFooterInfoItem | SbFooterNavigationSection,
) {
  return item.visible
}
</script>

<style lang="postcss" scoped>
.accordion-headline.active > .accordion-chevron {
  transform: rotate(180deg);
}

.accordion-headline.active + .accordion-content {
  display: block;
}
.footer-column:last-of-type {
  grid-column: 10 / 13;
}
</style>
